import { ATHLETE_PROFILE_SCHEMA_VERSION, buildPowerZonesFromFtp, createSeedAthleteProfile } from "./seedAthleteProfile";
import type { AthleteProfile, AthleteProfileSnapshot, FtpPolicySelection, LegacyAthleteSettingsV1 } from "./types/athleteProfile";

export const ATHLETE_PROFILE_STORAGE_KEY = "athlete.profile";
const LEGACY_SETTINGS_STORAGE_KEY = "jpl-settings";

export type StorageAdapter = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
};

export type AthleteProfileRepository = {
  loadProfile(): AthleteProfile;
  saveProfile(profile: AthleteProfile): AthleteProfile;
  resetProfile(): AthleteProfile;
};

class MemoryStorageAdapter implements StorageAdapter {
  private values = new Map<string, string>();

  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }

  removeItem(key: string): void {
    this.values.delete(key);
  }
}

export function createLocalStorageAdapter(): StorageAdapter {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }

  return new MemoryStorageAdapter();
}

function nowIso(): string {
  return new Date().toISOString();
}

function isValidPositiveNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function normalizePolicySelection(value: unknown, fallback: FtpPolicySelection): FtpPolicySelection {
  if (!value || typeof value !== "object") {
    return fallback;
  }

  const candidate = value as Partial<FtpPolicySelection> & { kind?: unknown };
  const kinds: FtpPolicySelection["kind"][] = ["athlete-profile-default", "indoor", "outdoor", "road", "gravel", "manual"];
  if (typeof candidate.kind !== "string" || !kinds.includes(candidate.kind as FtpPolicySelection["kind"])) {
    return fallback;
  }

  return {
    kind: candidate.kind as FtpPolicySelection["kind"],
    manualFtpWatts: isValidPositiveNumber(candidate.manualFtpWatts) ? Math.round(candidate.manualFtpWatts) : null
  };
}

function normalizeEffectiveDate(value: unknown, fallback: string | null): string | null {
  if (typeof value !== "string" || !value.trim()) {
    return fallback;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? fallback : new Date(parsed).toISOString();
}

function cloneProfile(profile: AthleteProfile): AthleteProfile {
  return JSON.parse(JSON.stringify(profile)) as AthleteProfile;
}

function normalizeProfile(profile: AthleteProfile): AthleteProfile {
  const seed = createSeedAthleteProfile();
  const nextProfile = cloneProfile(profile);
  const currentFtp = isValidPositiveNumber(nextProfile.performance.ftpWatts)
    ? Math.round(nextProfile.performance.ftpWatts)
    : isValidPositiveNumber(nextProfile.performance.roadFtpWatts)
      ? Math.round(nextProfile.performance.roadFtpWatts)
      : isValidPositiveNumber(nextProfile.performance.outdoorFtpWatts)
        ? Math.round(nextProfile.performance.outdoorFtpWatts)
        : seed.performance.ftpWatts;

  nextProfile.performance.ftpWatts = currentFtp;
  nextProfile.performance.ftpEffectiveDateIso = normalizeEffectiveDate(nextProfile.performance.ftpEffectiveDateIso, seed.performance.ftpEffectiveDateIso);
  nextProfile.performance.ftpSource = nextProfile.performance.ftpSource ?? seed.performance.ftpSource;
  nextProfile.performance.indoorFtpWatts = isValidPositiveNumber(nextProfile.performance.indoorFtpWatts) ? Math.round(nextProfile.performance.indoorFtpWatts) : seed.performance.indoorFtpWatts;
  nextProfile.performance.outdoorFtpWatts = isValidPositiveNumber(nextProfile.performance.outdoorFtpWatts) ? Math.round(nextProfile.performance.outdoorFtpWatts) : currentFtp;
  nextProfile.performance.roadFtpWatts = isValidPositiveNumber(nextProfile.performance.roadFtpWatts) ? Math.round(nextProfile.performance.roadFtpWatts) : nextProfile.performance.outdoorFtpWatts;
  nextProfile.performance.gravelFtpWatts = isValidPositiveNumber(nextProfile.performance.gravelFtpWatts) ? Math.round(nextProfile.performance.gravelFtpWatts) : seed.performance.gravelFtpWatts;
  nextProfile.performance.defaultAnalysisFtpPolicy = normalizePolicySelection(nextProfile.performance.defaultAnalysisFtpPolicy, seed.performance.defaultAnalysisFtpPolicy);
  nextProfile.performance.ftpHistory = Array.isArray(nextProfile.performance.ftpHistory) && nextProfile.performance.ftpHistory.length > 0 ? nextProfile.performance.ftpHistory : seed.performance.ftpHistory;
  nextProfile.performance.vo2Max = nextProfile.performance.vo2Max ?? null;
  nextProfile.performance.preferredCadenceRpm = nextProfile.performance.preferredCadenceRpm ?? seed.performance.preferredCadenceRpm;
  nextProfile.performance.primaryDiscipline = nextProfile.performance.primaryDiscipline ?? seed.performance.primaryDiscipline;
  nextProfile.training = { ...seed.training, ...nextProfile.training };
  nextProfile.nutrition = { ...seed.nutrition, ...nextProfile.nutrition };
  nextProfile.preferences = { ...seed.preferences, ...nextProfile.preferences };

  const hasPowerZones = Array.isArray(nextProfile.performance.powerZones) && nextProfile.performance.powerZones.length > 0;
  if (!hasPowerZones) {
    nextProfile.performance.powerZones = buildPowerZonesFromFtp(currentFtp);
  }

  if (!Array.isArray(nextProfile.performance.heartRateZones)) {
    nextProfile.performance.heartRateZones = [];
  }

  if (!nextProfile.medicalAndConsiderations || typeof nextProfile.medicalAndConsiderations !== "object") {
    nextProfile.medicalAndConsiderations = { ...seed.medicalAndConsiderations };
  } else {
    const legacyMedical = nextProfile.medicalAndConsiderations as AthleteProfile["medicalAndConsiderations"] & { activeMedications?: string[] };
    nextProfile.medicalAndConsiderations = {
      ...seed.medicalAndConsiderations,
      ...nextProfile.medicalAndConsiderations,
      currentMedications: nextProfile.medicalAndConsiderations.currentMedications ?? legacyMedical.activeMedications ?? []
    };
  }

  return nextProfile;
}

function migrateFromLegacySettings(legacy: LegacyAthleteSettingsV1): AthleteProfile {
  const seed = createSeedAthleteProfile();
  const ftp = typeof legacy.ftp === "number" && Number.isFinite(legacy.ftp) && legacy.ftp > 0
    ? Math.round(legacy.ftp)
    : seed.performance.ftpWatts;

  const weight = typeof legacy.weightLb === "number" && Number.isFinite(legacy.weightLb) && legacy.weightLb > 0
    ? legacy.weightLb
    : seed.personal.currentWeightLb;

  return {
    ...seed,
    personal: {
      ...seed.personal,
      currentWeightLb: weight
    },
    performance: {
      ...seed.performance,
      ftpWatts: ftp,
      ftpEffectiveDateIso: nowIso(),
      ftpSource: "legacy settings",
      indoorFtpWatts: ftp,
      outdoorFtpWatts: ftp,
      roadFtpWatts: ftp,
      gravelFtpWatts: ftp,
      defaultAnalysisFtpPolicy: { ...seed.performance.defaultAnalysisFtpPolicy },
      ftpHistory: [
        {
          effectiveDateIso: nowIso(),
          ftpWatts: ftp,
          source: "legacy settings",
          policyKind: "athlete-profile-default",
          note: "Migrated from legacy settings"
        }
      ],
      powerZones: buildPowerZonesFromFtp(ftp)
    },
    goals: {
      ...seed.goals,
      primaryEventName: typeof legacy.event === "string" && legacy.event.trim() ? legacy.event.trim() : seed.goals.primaryEventName,
      primaryEventDate: typeof legacy.eventDate === "string" && legacy.eventDate.trim() ? legacy.eventDate : seed.goals.primaryEventDate
    }
  };
}

function migrateFromV1Profile(value: unknown): AthleteProfile | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const profileV1 = value as Record<string, unknown>;
  const seed = createSeedAthleteProfile();

  if (!profileV1.personal || !profileV1.performance || !profileV1.goals) {
    return null;
  }

  const currentWeight = Number(profileV1.personal && (profileV1.personal as Record<string, unknown>).currentWeightLb);
  const roadFtp = Number(profileV1.performance && (profileV1.performance as Record<string, unknown>).roadFtpWatts);
  const gravelFtp = Number(profileV1.performance && (profileV1.performance as Record<string, unknown>).gravelFtpWatts);

  const migrated: AthleteProfile = {
    ...seed,
    personal: {
      ...seed.personal,
      ...(profileV1.personal as Partial<AthleteProfile["personal"]>),
      currentWeightLb: Number.isFinite(currentWeight) && currentWeight > 0 ? currentWeight : seed.personal.currentWeightLb,
      targetWeightLb:
        profileV1.personal &&
        typeof (profileV1.personal as Record<string, unknown>).targetWeightLb === "object" &&
        (profileV1.personal as Record<string, unknown>).targetWeightLb !== null
          ? (profileV1.personal as Record<string, unknown>).targetWeightLb as AthleteProfile["personal"]["targetWeightLb"]
          : seed.personal.targetWeightLb
    },
    performance: {
      ...seed.performance,
      ...(profileV1.performance as Partial<AthleteProfile["performance"]>),
      ftpWatts: isValidPositiveNumber(profileV1.performance && (profileV1.performance as Record<string, unknown>).ftpWatts) ? Math.round(Number((profileV1.performance as Record<string, unknown>).ftpWatts)) : seed.performance.ftpWatts,
      ftpEffectiveDateIso: normalizeEffectiveDate((profileV1.performance as Record<string, unknown>).ftpEffectiveDateIso, seed.performance.ftpEffectiveDateIso),
      ftpSource: (profileV1.performance as Record<string, unknown>).ftpSource as AthleteProfile["performance"]["ftpSource"] ?? seed.performance.ftpSource,
      indoorFtpWatts: isValidPositiveNumber((profileV1.performance as Record<string, unknown>).indoorFtpWatts) ? Math.round(Number((profileV1.performance as Record<string, unknown>).indoorFtpWatts)) : seed.performance.indoorFtpWatts,
      outdoorFtpWatts: isValidPositiveNumber(roadFtp) ? Math.round(roadFtp) : seed.performance.outdoorFtpWatts,
      roadFtpWatts: isValidPositiveNumber(roadFtp) ? Math.round(roadFtp) : seed.performance.roadFtpWatts,
      gravelFtpWatts: isValidPositiveNumber(gravelFtp) ? Math.round(gravelFtp) : seed.performance.gravelFtpWatts,
      defaultAnalysisFtpPolicy: normalizePolicySelection((profileV1.performance as Record<string, unknown>).defaultAnalysisFtpPolicy, seed.performance.defaultAnalysisFtpPolicy),
      ftpHistory: Array.isArray((profileV1.performance as Record<string, unknown>).ftpHistory) ? (profileV1.performance as Record<string, unknown>).ftpHistory as AthleteProfile["performance"]["ftpHistory"] : seed.performance.ftpHistory
    },
    training: {
      ...seed.training,
      ...(profileV1.training as Partial<AthleteProfile["training"]> ?? {})
    },
    goals: {
      ...seed.goals,
      ...(profileV1.goals as Partial<AthleteProfile["goals"]>)
    },
    nutrition: {
      ...seed.nutrition,
      ...(profileV1.nutrition as Partial<AthleteProfile["nutrition"]> ?? {})
    },
    recovery: {
      ...seed.recovery,
      ...(profileV1.recovery as Partial<AthleteProfile["recovery"]> ?? {})
    },
    medicalAndConsiderations: {
      ...seed.medicalAndConsiderations,
      ...(profileV1.medicalAndConsiderations as Partial<AthleteProfile["medicalAndConsiderations"]> ?? {}),
      currentMedications:
        (profileV1.medicalAndConsiderations as { currentMedications?: string[]; activeMedications?: string[] } | undefined)?.currentMedications ??
        (profileV1.medicalAndConsiderations as { activeMedications?: string[] } | undefined)?.activeMedications ??
        []
    },
    preferences: {
      ...seed.preferences,
      ...(profileV1.preferences as Partial<AthleteProfile["preferences"]> ?? {})
    }
  };

  if (!Array.isArray(migrated.performance.powerZones) || migrated.performance.powerZones.length === 0) {
    migrated.performance.powerZones = buildPowerZonesFromFtp(isValidPositiveNumber(roadFtp) ? Math.round(roadFtp) : seed.performance.ftpWatts);
  }

  if (!Array.isArray(migrated.performance.heartRateZones)) {
    migrated.performance.heartRateZones = [];
  }

  return migrated;
}

function parseProfileSnapshot(rawValue: string | null): AthleteProfile | null {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as AthleteProfileSnapshot | AthleteProfile;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    if ("profile" in parsed) {
      const snapshot = parsed as AthleteProfileSnapshot;
      if (snapshot.schemaVersion === ATHLETE_PROFILE_SCHEMA_VERSION && snapshot.profile) {
        return normalizeProfile(snapshot.profile);
      }

      const migrated = migrateFromV1Profile(snapshot.profile);
      return migrated ? normalizeProfile(migrated) : null;
    }

    const migrated = migrateFromV1Profile(parsed);
    return migrated ? normalizeProfile(migrated) : null;
  } catch {
    return null;
  }
}

function withSnapshot(profile: AthleteProfile): AthleteProfileSnapshot {
  return {
    schemaVersion: ATHLETE_PROFILE_SCHEMA_VERSION,
    savedAtIso: nowIso(),
    profile: normalizeProfile(profile)
  };
}

export function createAthleteProfileRepository(storageAdapter: StorageAdapter = createLocalStorageAdapter()): AthleteProfileRepository {
  function saveProfile(profile: AthleteProfile): AthleteProfile {
    const normalized = normalizeProfile(profile);
    storageAdapter.setItem(ATHLETE_PROFILE_STORAGE_KEY, JSON.stringify(withSnapshot(normalized)));
    return normalized;
  }

  function loadProfile(): AthleteProfile {
    const existingRaw = storageAdapter.getItem(ATHLETE_PROFILE_STORAGE_KEY);
    const parsedExisting = parseProfileSnapshot(existingRaw);

    if (parsedExisting) {
      if (!existingRaw) {
        saveProfile(parsedExisting);
      } else {
        try {
          const snapshot = JSON.parse(existingRaw) as { schemaVersion?: number };
          if (snapshot.schemaVersion !== ATHLETE_PROFILE_SCHEMA_VERSION) {
            saveProfile(parsedExisting);
          }
        } catch {
          saveProfile(parsedExisting);
        }
      }
      return parsedExisting;
    }

    const hadExistingValue = existingRaw !== null;

    const legacyRaw = storageAdapter.getItem(LEGACY_SETTINGS_STORAGE_KEY);
    if (!hadExistingValue && legacyRaw) {
      try {
        const legacyParsed = JSON.parse(legacyRaw) as LegacyAthleteSettingsV1;
        const migrated = migrateFromLegacySettings(legacyParsed);
        const saved = saveProfile(migrated);
        storageAdapter.removeItem?.(LEGACY_SETTINGS_STORAGE_KEY);
        return saved;
      } catch {
        // Fall through to seed.
      }
    }

    const seed = createSeedAthleteProfile();
    return saveProfile(seed);
  }

  function resetProfile(): AthleteProfile {
    const seed = createSeedAthleteProfile();
    return saveProfile(seed);
  }

  return {
    loadProfile,
    saveProfile,
    resetProfile
  };
}
